import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellRing, CalendarClock, Loader2, Pause, Play, Plus, RefreshCw, Trash2, X, Zap } from 'lucide-react';
import { withCsrfHeaders } from '../utils/csrf';
import '../styles/ProactiveAiPlanner.css';

const API_BASE = 'https://api.casedb.co';

const DEFAULT_HEALTH_PROMPT = 'Review this case for current posture, risks, stale work, missing facts or documents, and the next best actions. Use available case data, activity feed, deadlines, documents, and prior chat context where available. Return concise findings with confidence and source gaps.';

const scheduleLabels = {
    manual: 'Manual',
    once: 'Once',
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    interval_days: 'Every N days',
};

const taskTypeLabels = {
    case_health_assessment: 'Case health assessment',
    daily_outlook_digest: 'Daily Outlook summary',
    document_watch: 'Document watch',
    deadline_risk_review: 'Deadline risk review',
    draft_background_job: 'Background draft job',
    custom: 'Custom task',
};

const connectorManagedTaskTypes = new Set(['daily_outlook_digest']);

const formatDateTime = (value) => {
    if (!value) return 'Not scheduled';
    const date = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const taskScheduleSummary = (task) => {
    const kind = task?.schedule_kind || 'manual';
    const label = scheduleLabels[kind] || kind;
    if (kind === 'manual') return 'Manual run only';
    if (kind === 'interval_days') return `${label}${task?.cadence_value ? `: ${task.cadence_value} days` : ''}`;
    return `${label} · next ${formatDateTime(task?.next_run_at)}`;
};

const taskStatusLabel = (status) => {
    const normalized = String(status || 'active').toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const runStatusLabel = (status) => {
    const normalized = String(status || 'queued').toLowerCase();
    if (normalized === 'queued') return 'Queued';
    if (normalized === 'running') return 'Running';
    if (normalized === 'completed') return 'Completed';
    if (normalized === 'failed') return 'Failed';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const runTimeLabel = (run) => formatDateTime(run?.finished_at || run?.started_at || run?.queued_at || run?.created_at);

const ProactiveAiPlanner = ({ chatId = null, caseId = null, compact = false }) => {
    const [open, setOpen] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(() => ({
        title: 'Case health assessment',
        task_type: 'case_health_assessment',
        schedule_kind: 'daily',
        cadence_value: '',
        case_id: caseId || '',
        prompt: DEFAULT_HEALTH_PROMPT,
    }));

    const activeTasks = useMemo(() => tasks.filter(task => task.status === 'active'), [tasks]);
    const scopedTasks = useMemo(() => {
        const normalizedCaseId = Number(caseId || 0);
        const normalizedChatId = Number(chatId || 0);
        return tasks.filter((task) => {
            if (normalizedCaseId > 0 && Number(task.case_id || 0) === normalizedCaseId) return true;
            if (normalizedChatId > 0 && Number(task.chat_id || 0) === normalizedChatId) return true;
            return false;
        });
    }, [caseId, chatId, tasks]);

    useEffect(() => {
        setForm(prev => ({ ...prev, case_id: prev.case_id || caseId || '' }));
    }, [caseId]);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ limit: '80' });
            const response = await fetch(`${API_BASE}/proactive-ai-tasks.php?${params.toString()}`, { credentials: 'include' });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not load proactive AI tasks.');
            }
            setTasks(Array.isArray(payload.tasks) ? payload.tasks : []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load proactive AI tasks.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        loadTasks();
        const intervalId = window.setInterval(loadTasks, 30000);
        return () => window.clearInterval(intervalId);
    }, [loadTasks, open]);

    const postTaskAction = async (body, method = 'POST') => {
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`${API_BASE}/proactive-ai-tasks.php`, {
                method,
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, method),
                body: JSON.stringify(body),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Could not update proactive AI task.');
            }
            await loadTasks();
            return payload;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not update proactive AI task.');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleCreateTask = async (event) => {
        event.preventDefault();
        const normalizedCaseId = Number(form.case_id || 0);
        const body = {
            title: form.title,
            prompt: form.prompt,
            task_type: form.task_type,
            schedule_kind: form.schedule_kind,
            cadence_value: form.schedule_kind === 'interval_days' ? form.cadence_value : '',
            case_id: Number.isFinite(normalizedCaseId) && normalizedCaseId > 0 ? normalizedCaseId : null,
            chat_id: Number(chatId || 0) > 0 ? Number(chatId) : null,
            scope: {
                source: 'jane_planner',
                include_case_context: Number.isFinite(normalizedCaseId) && normalizedCaseId > 0,
                include_chat_context: Number(chatId || 0) > 0,
            },
            metadata: {
                created_from: 'jane_planner',
            },
        };
        const payload = await postTaskAction(body, 'POST');
        if (payload?.success) {
            setShowCreate(false);
            setForm(prev => ({ ...prev, title: 'Case health assessment', prompt: DEFAULT_HEALTH_PROMPT }));
        }
    };

    const displayedTasks = scopedTasks.length > 0 ? scopedTasks : tasks;

    return (
        <>
            <button
                type="button"
                className={`library-rag-header-action proactive-ai-toggle${open ? ' active' : ''}${compact ? ' compact' : ''}`}
                onClick={() => setOpen(true)}
                title="Open proactive AI planner"
                aria-label="Open proactive AI planner"
            >
                <BellRing size={16} />
                <span>{activeTasks.length > 0 ? `Proactive ${activeTasks.length}` : 'Proactive'}</span>
            </button>
            {open && (
                <div className="proactive-ai-planner-shell" role="dialog" aria-label="Proactive AI planner">
                    <button type="button" className="proactive-ai-planner-backdrop" onClick={() => setOpen(false)} aria-label="Close proactive AI planner" />
                    <aside className="proactive-ai-planner-panel">
                        <header className="proactive-ai-planner-header">
                            <div>
                                <div className="proactive-ai-planner-eyebrow">Proactive tasks enabled</div>
                                <h2>AI planner</h2>
                            </div>
                            <button type="button" className="proactive-ai-icon-button" onClick={() => setOpen(false)} aria-label="Close planner">
                                <X size={18} />
                            </button>
                        </header>

                        <div className="proactive-ai-planner-summary">
                            <span><Zap size={14} /> {activeTasks.length} active</span>
                            <span><CalendarClock size={14} /> {tasks.length} planned</span>
                            <button type="button" onClick={loadTasks} disabled={loading}>
                                {loading ? <Loader2 size={14} className="spinner" /> : <RefreshCw size={14} />}
                                Refresh
                            </button>
                        </div>

                        {error && <div className="proactive-ai-error">{error}</div>}

                        <button type="button" className="proactive-ai-create-toggle" onClick={() => setShowCreate(prev => !prev)}>
                            <Plus size={16} /> {showCreate ? 'Hide new task' : 'Plan new task'}
                        </button>

                        {showCreate && (
                            <form className="proactive-ai-create-form" onSubmit={handleCreateTask}>
                                <label>
                                    <span>Title</span>
                                    <input value={form.title} onChange={event => setForm(prev => ({ ...prev, title: event.target.value }))} />
                                </label>
                                <label>
                                    <span>Type</span>
                                    <select value={form.task_type} onChange={event => setForm(prev => ({ ...prev, task_type: event.target.value }))}>
                                        {Object.entries(taskTypeLabels)
                                            .filter(([value]) => !connectorManagedTaskTypes.has(value))
                                            .map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </label>
                                <div className="proactive-ai-form-row">
                                    <label>
                                        <span>Schedule</span>
                                        <select value={form.schedule_kind} onChange={event => setForm(prev => ({ ...prev, schedule_kind: event.target.value }))}>
                                            {Object.entries(scheduleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                        </select>
                                    </label>
                                    <label>
                                        <span>Case ID</span>
                                        <input type="number" value={form.case_id} onChange={event => setForm(prev => ({ ...prev, case_id: event.target.value }))} placeholder="Optional" />
                                    </label>
                                </div>
                                {form.schedule_kind === 'interval_days' && (
                                    <label>
                                        <span>Repeat every</span>
                                        <input type="number" min="1" max="365" value={form.cadence_value} onChange={event => setForm(prev => ({ ...prev, cadence_value: event.target.value }))} placeholder="Days" />
                                    </label>
                                )}
                                <label>
                                    <span>Prompt</span>
                                    <textarea rows={6} value={form.prompt} onChange={event => setForm(prev => ({ ...prev, prompt: event.target.value }))} />
                                </label>
                                <button type="submit" className="action alt" disabled={saving || form.prompt.trim().length < 8}>
                                    {saving ? <Loader2 size={15} className="spinner" /> : <Plus size={15} />}
                                    Save task
                                </button>
                            </form>
                        )}

                        <div className="proactive-ai-task-list">
                            {loading && displayedTasks.length === 0 ? (
                                <div className="proactive-ai-empty"><Loader2 size={16} className="spinner" /> Loading planned work…</div>
                            ) : displayedTasks.length === 0 ? (
                                <div className="proactive-ai-empty">No proactive AI tasks yet. Plan one to keep Jane watching this case or chat.</div>
                            ) : displayedTasks.map((task) => (
                                <article key={task.id} className={`proactive-ai-task ${task.status}`}>
                                    <div className="proactive-ai-task-topline">
                                        <span>{taskTypeLabels[task.task_type] || task.task_type}</span>
                                        <span className={`proactive-ai-status ${task.status}`}>{taskStatusLabel(task.status)}</span>
                                    </div>
                                    <h3>{task.title}</h3>
                                    <p>{connectorManagedTaskTypes.has(task.task_type)
                                        ? 'Jane reviews recent Outlook messages and adds the private summary to your Jane conversation.'
                                        : task.prompt}</p>
                                    <div className="proactive-ai-task-meta">
                                        <span>{taskScheduleSummary(task)}</span>
                                        {connectorManagedTaskTypes.has(task.task_type) && <span>Managed by Jane</span>}
                                        {task.case_id && <span>Case #{task.case_id}</span>}
                                        {task.chat_id && <span>Chat #{task.chat_id}</span>}
                                    </div>
                                    {connectorManagedTaskTypes.has(task.task_type) ? (
                                        <div className="proactive-ai-task-actions">
                                            <button type="button" onClick={() => window.location.assign(task.chat_id ? `/ai?chat_id=${task.chat_id}` : '/ai')}>
                                                Manage with Jane
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="proactive-ai-task-actions">
                                            <button type="button" onClick={() => postTaskAction({ action: 'run_now', id: task.id })} disabled={saving}>
                                                <Play size={14} /> Run now
                                            </button>
                                            {task.status === 'active' ? (
                                                <button type="button" onClick={() => postTaskAction({ action: 'pause', id: task.id })} disabled={saving}>
                                                    <Pause size={14} /> Pause
                                                </button>
                                            ) : task.status === 'paused' ? (
                                                <button type="button" onClick={() => postTaskAction({ action: 'resume', id: task.id })} disabled={saving}>
                                                    <Play size={14} /> Resume
                                                </button>
                                            ) : null}
                                            <button type="button" className="danger" onClick={() => postTaskAction({ action: 'delete', id: task.id }, 'DELETE')} disabled={saving}>
                                                <Trash2 size={14} /> Delete
                                            </button>
                                        </div>
                                    )}
                                    {Array.isArray(task.runs) && task.runs.length > 0 && (
                                        <div className="proactive-ai-run-list" aria-label={`Recent runs for ${task.title}`}>
                                            {task.runs.slice(0, 4).map((run) => (
                                                <div key={`proactive-run-${task.id}-${run.id}`} className={`proactive-ai-run ${run.status || 'queued'}`}>
                                                    <span className="proactive-ai-run-status">{runStatusLabel(run.status)}</span>
                                                    <span className="proactive-ai-run-message">{run.status_message || run.summary || run.error_message || 'Run queued.'}</span>
                                                    <span className="proactive-ai-run-time">{runTimeLabel(run)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </article>
                            ))}
                        </div>
                    </aside>
                </div>
            )}
        </>
    );
};

export default ProactiveAiPlanner;
